import React, { useState } from 'react'
import { Row, Col, Alert } from 'reactstrap'
import { useApolloClient, gql, useQuery } from '@apollo/client'
import CustomYupInputForm from '../../Material/CustomYupInputForm'
import { SubrogationClaimSchema } from '../IncidentModals/IncidentModalsSchema'

const SUBROGATION_STATUS_CHOICES = gql`
  query SubrogationStatusChoices {
    subrogationStatusChoices: __type(name: "IncidentsSubrogationClaimsStatusChoices") {
      enumValues {
        name
        description
      }
    }
  }
`

const UPDATE_SUBROGATION_CLAIM = gql`
  mutation updateSubrogationClaim($input: UpdateSubrogationClaimMutationInput!) {
    updateSubrogationClaim(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const ALL_TPAS = gql`
  query AllTpas($incidentId: ID!) {
    allTpas (incidentId: $incidentId, claimType: "SUBROGATION") {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`

const SubrogationClaimInfo = ({ claim, refetch, incidentId }) => {
    const apolloClient = useApolloClient()
    const { data: statusChoicesData } = useQuery(SUBROGATION_STATUS_CHOICES)
    const { data: tpaData } = useQuery(ALL_TPAS, {
        variables: { incidentId }
    })
    const statusOptions = statusChoicesData?.subrogationStatusChoices?.enumValues?.map(opt => ({
        label: opt.description || opt.name,
        value: opt.name
    })) || []
    const tpaOptions = tpaData?.allTpas?.edges?.map(edge => ({
        label: edge.node.name,
        value: edge.node.id
    })) || []

    const [editField, setEditField] = useState({ claimId: null, field: null })
    const [inputValues, setInputValues] = useState({})
    const [fieldLoading, setFieldLoading] = useState({})
    const [originalValues, setOriginalValues] = useState({})
    const [fieldErrors, setFieldErrors] = useState({})
    const [error, setError] = useState(null)

    const editFieldHandler = (fieldName, fieldValue = "", claimId = null) => {
        setEditField({ claimId, field: fieldName })
        setInputValues(prev => ({ ...prev, [fieldName]: fieldValue }))
        setOriginalValues(prev => ({ ...prev, [fieldName]: fieldValue }))
        setError(null)
    }
    const saveFieldHandler = (subrogationClaimId, fieldName, fieldValue) => {
        if (fieldErrors[fieldName]) return
        if (fieldValue === undefined || fieldValue === null || fieldValue === "") {
            cancelEditHandler()
            return
        }
        updateSubrogationClaimHandler(subrogationClaimId, fieldName, fieldValue)
    }
    const updateInputHandler = (e) => {
        const rawValue = e.target.value
        const fieldName = e.target.name
        const fieldType = e.target.type
        let value = rawValue
        if (["daysDown"].includes(fieldName)) {
            value = rawValue === '' ? '' : parseInt(rawValue, 10)
            if (isNaN(value)) value = ''
        }
        setInputValues(prev => ({
            ...prev,
            [fieldName]: fieldType === "select" ? rawValue === "true" ? true : rawValue === "false" ? false : rawValue : value
        }))
    }
    const cancelEditHandler = () => {
        setEditField({ claimId: null, field: null })
        setInputValues({})
        setOriginalValues({})
        setFieldErrors({})
        setError("")
        setFieldLoading({})
    }
    const updateSubrogationClaimHandler = async (subrogationClaimId, name, value) => {
        try {
            const trimmedValue = typeof value === 'string' ? value.trim() : value
            if (trimmedValue === originalValues[name]) {
                cancelEditHandler()
                return
            }
            const isValidValue = typeof trimmedValue === "boolean" || Boolean(trimmedValue)
            if (!subrogationClaimId || !name || !isValidValue) return
            setFieldLoading(prev => ({ ...prev, [name]: true }))
            setError("")
            const input = {
                claimId: subrogationClaimId,
                [name]: trimmedValue
            }
            try {
                const result = await apolloClient.mutate({
                    mutation: UPDATE_SUBROGATION_CLAIM,
                    variables: { input },
                })
                const { ok, errors } = result?.data?.updateSubrogationClaim || {}
                if (ok) {
                    setEditField({ claimId: null, field: null })
                    setError("")
                    setInputValues(prev => ({ ...prev, [name]: trimmedValue }))
                    setOriginalValues(prev => ({ ...prev, [name]: trimmedValue }))
                    refetch()
                } else {
                    const errorMessage = errors?.[0]?.messages?.toString() ||
                        "Something went wrong, Please contact admin for support!"
                    setError(errorMessage)
                }
            } catch (err) {
                setError(err.message)
            }
        } catch (err) {
            setError(err.message)
        } finally {
            setEditField({ claimId: null, field: null })
            setFieldLoading(prev => ({ ...prev, [name]: false }))
            setFieldErrors(prev => ({ ...prev, [name]: "" }))
        }
    }
    return (
        <Row>
            {error && <Col xs={12}><Alert color="danger">{error}</Alert></Col>}
            <Col xs={4} className="mb-2">
                <CustomYupInputForm
                    label="Status"
                    fieldName={"status"}
                    inputType="select"
                    options={statusOptions}
                    showEdit
                    isEditing={editField.claimId === claim.id && editField.field === "status"}
                    isLoading={!!fieldLoading?.status}
                    inputValue={inputValues?.["status"]}
                    defaultValue={claim?.status}
                    onEdit={() => editFieldHandler("status", claim.status, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.status}
                />
            </Col>
            <Col xs={4} className="mb-2">
                <CustomYupInputForm
                    label="TPA"
                    fieldName={"tpaId"}
                    inputType="select"
                    options={tpaOptions}
                    showEdit
                    isEditing={editField.claimId === claim.id && editField.field === "tpaId"}
                    isLoading={!!fieldLoading?.tpa}
                    inputValue={inputValues?.["tpaId"]}
                    defaultValue={claim?.tpa?.id}
                    onEdit={() => editFieldHandler("tpaId", claim.tpa?.id, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.tpa}
                />
            </Col>
            <Col xs={4} className="mb-2">
                <CustomYupInputForm
                    label="TPA Fee"
                    fieldName={"tpaFee"}
                    inputType="number"
                    showEdit
                    isEditing={editField.claimId === claim.id && editField.field === "tpaFee"}
                    isLoading={!!fieldLoading?.tpaFee}
                    inputValue={inputValues?.["tpaFee"]}
                    defaultValue={claim?.tpaFee}
                    onEdit={() => editFieldHandler("tpaFee", claim.tpaFee, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.tpaFee}
                />
            </Col>
            <Col xs={4} className="mb-2">
                <CustomYupInputForm
                    label="Estimated Repair"
                    fieldName={"estimatedRepair"}
                    inputType="number"
                    showEdit
                    isEditing={editField.claimId === claim.id && editField.field === "estimatedRepair"}
                    isLoading={!!fieldLoading?.estimatedRepair}
                    inputValue={inputValues?.["estimatedRepair"]}
                    defaultValue={claim?.estimatedRepair}
                    onEdit={() => editFieldHandler("estimatedRepair", claim.estimatedRepair, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.estimatedRepair}
                />
            </Col>
            <Col xs={4} className="mb-2">
                <CustomYupInputForm
                    label="Estimated Amount"
                    fieldName={"estimatedAmount"}
                    inputType="number"
                    showEdit
                    isEditing={editField.claimId === claim.id && editField.field === "estimatedAmount"}
                    isLoading={!!fieldLoading?.estimatedAmount}
                    inputValue={inputValues?.["estimatedAmount"]}
                    defaultValue={claim?.estimatedAmount}
                    onEdit={() => editFieldHandler("estimatedAmount", claim.estimatedAmount, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.estimatedAmount}
                />
            </Col>
            <Col xs={4} className="mb-2">
                <CustomYupInputForm
                    label="Requested Amount"
                    fieldName={"requestedAmount"}
                    inputType="number"
                    showEdit
                    isEditing={editField.claimId === claim.id && editField.field === "requestedAmount"}
                    isLoading={!!fieldLoading?.requestedAmount}
                    inputValue={inputValues?.["requestedAmount"]}
                    defaultValue={claim?.requestedAmount}
                    onEdit={() => editFieldHandler("requestedAmount", claim.requestedAmount, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.requestedAmount}
                />
            </Col>
            <Col xs={4} className="mb-2">
                <CustomYupInputForm
                    label="Collected Amount"
                    fieldName={"collectedAmount"}
                    inputType="number"
                    showEdit
                    isEditing={editField.claimId === claim.id && editField.field === "collectedAmount"}
                    isLoading={!!fieldLoading?.collectedAmount}
                    inputValue={inputValues?.["collectedAmount"]}
                    defaultValue={claim?.collectedAmount}
                    onEdit={() => editFieldHandler("collectedAmount", claim.collectedAmount, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.collectedAmount}
                />
            </Col>
            <Col xs={4} className="mb-2">
                <CustomYupInputForm
                    label="Diminished Value"
                    fieldName={"diminishedValue"}
                    inputType="number"
                    showEdit
                    isEditing={editField.claimId === claim.id && editField.field === "diminishedValue"}
                    isLoading={!!fieldLoading?.diminishedValue}
                    inputValue={inputValues?.["diminishedValue"]}
                    defaultValue={claim?.diminishedValue}
                    onEdit={() => editFieldHandler("diminishedValue", claim.diminishedValue, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.diminishedValue}
                />
            </Col>
            <Col xs={4} className="mb-2">
                <CustomYupInputForm
                    label="Sent Date"
                    fieldName={"sentDate"}
                    inputType="date"
                    showEdit
                    isEditing={editField.claimId === claim.id && editField.field === "sentDate"}
                    isLoading={!!fieldLoading?.sentDate}
                    inputValue={inputValues?.["sentDate"]}
                    defaultValue={claim?.sentDate}
                    onEdit={() => editFieldHandler("sentDate", claim.sentDate, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.sentDate}
                />
            </Col>
            <Col xs={4} className="mb-2">
                <CustomYupInputForm
                    label="Filed Date"
                    fieldName={"filedDate"}
                    inputType="date"
                    showEdit
                    isEditing={editField.claimId === claim.id && editField.field === "filedDate"}
                    isLoading={!!fieldLoading?.filedDate}
                    inputValue={inputValues?.["filedDate"]}
                    defaultValue={claim?.filedDate}
                    onEdit={() => editFieldHandler("filedDate", claim.filedDate, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.filedDate}
                />
            </Col>
            <Col xs={4} className="mb-2">
                <CustomYupInputForm
                    label="Daily Rate"
                    fieldName={"dailyRate"}
                    inputType="number"
                    showEdit
                    isEditing={editField.claimId === claim.id && editField.field === "dailyRate"}
                    isLoading={!!fieldLoading?.dailyRate}
                    inputValue={inputValues?.["dailyRate"]}
                    defaultValue={claim?.dailyRate}
                    onEdit={() => editFieldHandler("dailyRate", claim.dailyRate, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.dailyRate}
                />
            </Col>
            <Col xs={4} className="mb-2">
                <CustomYupInputForm
                    label="Days Down"
                    fieldName={"daysDown"}
                    inputType="number"
                    showEdit
                    isEditing={editField.claimId === claim.id && editField.field === "daysDown"}
                    isLoading={!!fieldLoading?.daysDown}
                    inputValue={inputValues?.["daysDown"]}
                    defaultValue={claim?.daysDown}
                    onEdit={() => editFieldHandler("daysDown", claim.daysDown, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.daysDown}
                />
            </Col>
            <Col xs={4} className="mb-2">
                <CustomYupInputForm
                    label="Loss Of Use"
                    fieldName={"lossOfUse"}
                    inputType="number"
                    defaultValue={claim?.lossOfUse}
                />
            </Col>
            <Col xs={8} className="mb-2">
                <CustomYupInputForm
                    label="Notes"
                    fieldName={"notes"}
                    inputType="text"
                    showEdit
                    isEditing={editField.claimId === claim.id && editField.field === "notes"}
                    isLoading={!!fieldLoading?.notes}
                    inputValue={inputValues?.["notes"]}
                    defaultValue={claim?.notes}
                    onEdit={() => editFieldHandler("notes", claim.notes, claim.id)}
                    onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                    onChange={updateInputHandler}
                    onCancel={cancelEditHandler}
                    yupSchema={SubrogationClaimSchema.fields.notes}
                />
            </Col>
        </Row>
    )
}

export default SubrogationClaimInfo
